import { useTranslation } from '~/shared/utils/translation';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import styles from './BasketSummary.module.scss';
import { PriceSection } from '~/shared/components/Summary/components/PriceSection';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import Typography from '~/shared/components/Typography/Typography';
import { theme } from '~/theme';
import { Spacer } from '~/theme/design-system/components/Spacer';
import { PromoCodeSection } from '~/shared/components/Summary/GiftcardSection/PromoCodeSection';
import { BonusRewardsSection } from '~/shared/components/Summary/components/BonusRewardsSection/BonusRewardsSection';
import { useMemberAccountInformation } from '~/shared/hooks/loyalty';

type ComponentProps = {
    onSubmit: () => void;
    disabledButton?: boolean;
    className?: string;
    membership: boolean;
    setMembership: (membership: boolean) => void;
};

export const BasketSummary = ({
    onSubmit,
    disabledButton,
    membership,
    setMembership,
}: ComponentProps) => {
    const { translate } = useTranslation();

    const { isPlusMember } = useCustomer();
    const { handlers } = useProducts();

    const freeShipping = handlers.getHasFreeShipping();
    const amountToFreeShipping = handlers.getAmountToFreeShipping();
    const { data: memberAccountData } = useMemberAccountInformation({
        select: (memberData) => ({
            sumOfCoupons: memberData.value[0].sumOfCoupons,
            qtyOfVouchers: memberData.value[0].qtyOfVouchers,
        }),
    });

    const formatAddon = (value?: number, suffix = '') => (value ? `${value} ${suffix}` : undefined);

    return (
        <div className={styles.basketSummaryWrapper}>
            <Typography variant="title-S" as="h1" weight="bold">
                {translate('basket.priceSectionTitle')}
            </Typography>
            <Spacer space={theme.space[4]} />
            <PriceSection
                ctaLabel={translate('basket.basket.goToCheckout')}
                onCtaHandler={onSubmit}
                disabledButton={disabledButton}
                fullMode={true}
                isRegistered={isPlusMember}
                freeShipping={freeShipping}
                amountToFreeShipping={amountToFreeShipping}
                membership={membership}
                setMembership={setMembership}
            >
                <Spacer space={theme.space[4]} />
                <PromoCodeSection
                    discountCodeTitle={translate('checkout.promoSection.discountCodeTitle')}
                    giftCardTitle={translate('checkout.promoSection.giftCardTitle')}
                    titleAddon={formatAddon(memberAccountData?.qtyOfVouchers, 'stk.')}
                    submitLabel={translate('checkout.promoSection.submitLabel')}
                />
                {isPlusMember && (memberAccountData?.sumOfCoupons ?? 0) > 0 ? (
                    <BonusRewardsSection
                        title={translate('checkout.bonusRewards.title')}
                        titleAddon={formatAddon(memberAccountData?.sumOfCoupons, 'kr.')}
                    />
                ) : null}
            </PriceSection>
        </div>
    );
};
